/**
 * Boot an arcade game: Phaser config (720-wide design, EXPAND), telemetry, attribution, app_open.
 * Usage in src/games/<id>/main.ts:  boot({ id: "sort", scenes: [SortScene] });
 */
import Phaser from "phaser";
import { GAME_IDS, type GameId } from "@enjoyment/contracts";
import { setupAccount } from "./account";
import { initTelemetry, track } from "./telemetry";
import { captureAttribution } from "@shared/growth";
import { W, BG } from "./ui";
import { currentLang, pick } from "./lang";

export interface BootOptions {
  id: string;
  scenes: Phaser.Types.Scenes.SceneType[];
  background?: number;
  /** Screen-reader instructions for the canvas; defaults to the page's meta description. */
  instructions?: string;
}

export function boot(opts: BootOptions): Phaser.Game {
  initTelemetry(opts.id);
  const attr = captureAttribution();
  track("app_open", { ref: attr?.ref ?? "", source: attr?.source ?? "", standalone: matchMedia("(display-mode: standalone)").matches });
  // Phone login + cloud save for this game (kit/account.ts). Nothing goes online until its map (or top bar) opens.
  if ((GAME_IDS as readonly string[]).includes(opts.id)) setupAccount({ games: [opts.id as GameId], surface: "game", track, lang: currentLang() });
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "app",
    width: W,
    height: 1280,
    backgroundColor: `#${(opts.background ?? BG).toString(16).padStart(6, "0")}`,
    scale: { mode: Phaser.Scale.EXPAND, autoCenter: Phaser.Scale.CENTER_BOTH },
    render: { antialias: true },
    input: { activePointers: 2 },
    scene: opts.scenes,
  });
  game.events.once("ready", () => {
    const canvas = game.canvas;
    canvas.tabIndex = 0;
    canvas.setAttribute("aria-label", `${document.title} ${pick(currentLang(), { tr: "oyun alanı", en: "game area" })}`);
    const instructions = document.createElement("p");
    instructions.id = `${opts.id}-game-instructions`;
    instructions.className = "sr-only";
    instructions.textContent = opts.instructions ?? document.querySelector('meta[name="description"]')?.getAttribute("content") ?? `${document.title} oyun açıklaması`;
    document.body.append(instructions);
    canvas.setAttribute("aria-describedby", instructions.id);
  });

  if (import.meta.env.PROD && "serviceWorker" in navigator && (location.protocol === "https:" || location.hostname === "localhost")) {
    const register = () => void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    if (document.readyState === "complete") register();
    else window.addEventListener("load", register, { once: true });
  }
  return game;
}

export { exposeBotHook } from "./botHook";

/** Persist small per-game progress in localStorage (offline-first). */
export function loadSave<T>(id: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`arcade.${id}`);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<T>) } : fallback;
  } catch {
    return fallback;
  }
}
export function writeSave<T>(id: string, data: T): void {
  try {
    localStorage.setItem(`arcade.${id}`, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}
